import {
  DashboardDefinition,
  DashboardWidgetConfiguration,
  ViewConfiguration,
} from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.types";
import { RelatedWidgetConfiguration } from "src/dynamic-view-renderer/Widgets/RelatedCases/relatedCases.typings";
import { WidgetDefinition } from "src/dynamic-view-renderer/Widgets/Widget.typings";

export const DEFAULT_MAX_RELATED_CASES = 20;
export const MAX_PRINTABLE_RELATED_CASES = 100;

export const isPrintableWidget = (definition: WidgetDefinition) =>
  [undefined, "basic", "rules", "alertWorkflow", "chronology", "table"].includes(definition.type);

export type PrintableConfigError =
  | { type: "no_dashboards" }
  | { type: "dashboard_not_found"; code: string }
  | { type: "dashboard_empty"; code: string }
  | { type: "widget_not_found"; widgetCode: string }
  | { type: "widget_not_printable"; widgetCode: string; widgetType: string; title: string }
  | { type: "page_break_invalid"; widgetCode: string }
  | { type: "max_related_not_table"; widgetCode: string }
  | { type: "max_related_out_of_range"; widgetCode: string; max: number }
  | { type: "table_missing_columns"; widgetCode: string };

export function getPrintableConfigurationErrors(
  view: ViewConfiguration,
  dashboards: DashboardDefinition[],
  widgets: WidgetDefinition[]
): PrintableConfigError[] {
  if (!view.dashboards.length) return [{ type: "no_dashboards" }];

  return view.dashboards.flatMap((code) => validateDashboard(code, dashboards, widgets));
}

function validateDashboard(
  code: string,
  dashboards: DashboardDefinition[],
  widgets: WidgetDefinition[]
): PrintableConfigError[] {
  const dashboard = dashboards.find((d) => d.code === code);
  if (!dashboard) return [{ type: "dashboard_not_found", code }];

  const errors: PrintableConfigError[] = [];

  const allWidgets = dashboard.rows.flatMap((row) => row.widgets);

  if (!allWidgets.length) {
    errors.push({ type: "dashboard_empty", code });
  }

  return [...errors, ...allWidgets.flatMap((config) => validateWidget(config, widgets))];
}

function validateWidget(
  config: DashboardWidgetConfiguration,
  widgets: WidgetDefinition[]
): PrintableConfigError[] {
  const errors: PrintableConfigError[] = [];
  const widget = widgets.find((w) => w.code === config.widgetCode);

  if (!widget) {
    return [{ type: "widget_not_found", widgetCode: config.widgetCode }];
  }

  if (!isPrintableWidget(widget)) {
    errors.push({
      type: "widget_not_printable",
      widgetCode: config.widgetCode,
      widgetType: widget.type ?? "",
      title: widget.title,
    });
  }

  if (
    config.printPageBreakBefore !== undefined &&
    typeof config.printPageBreakBefore !== "boolean"
  ) {
    errors.push({ type: "page_break_invalid", widgetCode: config.widgetCode });
  }

  if (config.maxRelatedCases !== undefined) {
    if (widget.type !== "table") {
      errors.push({ type: "max_related_not_table", widgetCode: config.widgetCode });
    } else if (
      !Number.isInteger(config.maxRelatedCases) ||
      config.maxRelatedCases < 1 ||
      config.maxRelatedCases > MAX_PRINTABLE_RELATED_CASES
    ) {
      errors.push({
        type: "max_related_out_of_range",
        widgetCode: config.widgetCode,
        max: MAX_PRINTABLE_RELATED_CASES,
      });
    }
  }

  if (widget.type === "table") {
    const conf = widget.configuration?.customConfiguration as
      | RelatedWidgetConfiguration
      | undefined;
    if (!Array.isArray(conf?.caseColumns) || !conf.caseColumns.length) {
      errors.push({ type: "table_missing_columns", widgetCode: config.widgetCode });
    }
  }

  return errors;
}
